import React, { useMemo, useRef } from 'react'
import styled from 'styled-components'
import ReactECharts from 'echarts-for-react'
import * as echarts from 'echarts'
import { Carousel, Empty, Tooltip, Typography } from 'antd'
import EmptyImg from '@/assets/imges/Empty/Empty.png'
import EmptyBarGraph from '@/assets/imges/Empty/EmptyBarGraph_y.png'
import 'swiper/swiper-bundle.css'
import 'swiper/components/pagination/pagination.less'
import { IconFont, SvgIcon } from '@/components'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Autoplay, Navigation, Pagination } from 'swiper'
import 'swiper/swiper-bundle.css'
import 'swiper/components/pagination/pagination.less'
import { t } from '@/i18n'
SwiperCore.use([Autoplay, Navigation, Pagination])

interface PropsType {
  taskScore: { score: number; risk_level: string }
  attachmentTypeRanking: {
    attachmentType: string
    rate: number
  }[]
  verifyResult: {
    interceptRate: number
    failRate: number
    detectRate: number
  }
  attackProtectionRanking: {
    attackName: string
    rate: number
  }[]
  riskLevelDistribution: {
    level: string
    interceptRate: number
    failRate: number
    detectRate: number
    failCount: number
    interceptCount: number
    detectCount: number
  }[]
  deviceProtectionRanking: {
    deviceName: string
    deviceIp: string
    interceptionRate: number
    detectionRate: number
  }[]
  templateProtectionFailureRanking: {
    name: string
    interceptRate: number
    detectRate: number
    failRate: number
    interceptCount: number
    detectCount: number
    failCount: number
  }[]
  caseLevelDistribution: {
    level: string
    interceptCount: number
    detectCount: number
    failCount: number
    interceptRate: number
    detectRate: number
    failRate: number
  }[]
}

const getRiskLevelMap = () => ({
  1: t('低危'),
  2: t('中危'),
  3: t('高危'),
  4: t('严重'),
})

const getCaseLevelMap = () => ({
  1: t('初级'),
  2: t('中级'),
  3: t('高级'),
})

const TaskCharts: React.FC<PropsType> = ({
  taskScore,
  verifyResult,
  attackProtectionRanking,
  attachmentTypeRanking,
  riskLevelDistribution = [],
  deviceProtectionRanking,
  templateProtectionFailureRanking,
  caseLevelDistribution,
}) => {
  console.log(taskScore, 'taskScore')
  const chunkSize = 5
  const score = useMemo(() => taskScore, [taskScore])
  const verifyResultData = useMemo(() => verifyResult, [verifyResult])
  const caseLevelData = useMemo(() => caseLevelDistribution, [caseLevelDistribution])
  const riskLevelData = useMemo(() => riskLevelDistribution, [riskLevelDistribution])
  const templateProtectionData = useMemo(
    () => templateProtectionFailureRanking,
    [templateProtectionFailureRanking],
  )
  const deviceCount = useMemo(() => deviceProtectionRanking.length, [deviceProtectionRanking])
  const deviceProtectionData = useMemo(() => deviceProtectionRanking, [deviceProtectionRanking])
  const scoreOption = (values: { value: number; title: string }) => {
    const { value, title } = values
    return {
      series: [
        {
          type: 'gauge',
          min: 0,
          max: 100,
          splitNumber: 5,
          radius: '100%',
          axisLine: {
            lineStyle: {
              width: 12,
              color: [
                [0, '#B70001'],
                [0.2, '#B70001'],
                [0.4, '#FF2F56'],
                [0.6, '#FFA70B'],
                [0.8, '#74A1FA'],
                [1, '#67C23A'],
              ],
            },
          },
          pointer: {
            icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
            length: '15%',
            width: 10,
            offsetCenter: [0, -45],
            itemStyle: {
              color: 'auto',
            },
          },
          axisTick: {
            length: 5,
            lineStyle: {
              color: 'auto',
              width: 1,
            },
            distance: 3,
          },
          splitLine: {
            length: 8,
            distance: 2,
            lineStyle: {
              color: 'auto',
              width: 2,
            },
          },
          axisLabel: {
            color: '#666666',
            fontSize: 8,
            distance: 15,
          },
          title: {
            offsetCenter: [0, 20],
            fontSize: 16,
            color: 'auto',
          },
          detail: {
            fontSize: 30,
            offsetCenter: [0, -20],
            valueAnimation: true,
            formatter: '{value}',
            color: 'auto',
          },
          data: [
            {
              value: value,
              name: title,
            },
          ],
        },
      ],
    }
  }

  const failRateOption = () => ({
    tooltip: {
      trigger: 'item',
      formatter: function (params: any) {
        return `${params.name}: ${params.value}%`
      },
    },
    legend: {
      selectedMode: false,
      bottom: '0',
      left: 'center',
    },
    title: {
      text: t('防护失效'),
      subtext: `${verifyResultData?.failRate / 100 || 0}%`,
      left: 'center',
      top: '35%',
      textStyle: {
        color: '#333',
        fontSize: 18,
        align: 'center',
      },
      subtextStyle: {
        color: '#333',
        fontSize: 24,
        fontWeight: 500,
      },
    },
    series: [
      {
        type: 'pie',
        top: '-15%',
        radius: ['45%', '70%'],
        avoidLabelOverlap: false,
        label: {
          show: false,
          position: 'center',
        },
        itemStyle: {
          normal: {
            color: function (colors) {
              let colorList = ['#5677D9', '#EE6666', '#88C56B']
              return colorList[colors.dataIndex]
            },
          },
        },
        labelLine: {
          show: false,
        },
        data:
          deviceCount > 0
            ? [
                {
                  value: verifyResultData?.interceptRate / 100,
                  name: t('拦截成功'),
                },
                {
                  value: verifyResultData?.failRate / 100,
                  name: t('防护失效'),
                },
                {
                  value: verifyResultData?.detectRate / 100,
                  name: t('检测成功'),
                },
              ]
            : verifyResultData?.interceptRate || verifyResultData?.failRate
            ? [
                {
                  value: verifyResultData?.interceptRate / 100,
                  name: t('拦截成功'),
                },
                {
                  value: verifyResultData?.failRate / 100,
                  name: t('防护失效'),
                },
              ]
            : [],
      },
    ],
  })

  const caseLevelFailOption = () => ({
    tooltip: {
      trigger: 'item',
      confine: true,
      axisPointer: {
        type: 'shadow',
      },
      formatter: function (params: any) {
        console.log(params, 'params')
        const data = params.data
        return `<div>
            <div style="margin-bottom: 4px">${data.name}</div>
            <div style="margin-bottom: 4px; color: #666;">
              <span>{t('防护失效数')}: </span>
              <span style="font-weight: 500;">${data.failCount}</span>
            </div>
            <div>
              <span style="display: inline-block; margin-right: 8px; width: 6px; height: 6px; background-color: ${params.color}; border-radius: 50%;"></span>
              <span style="color: #666;">{t('防护失效率')}</span>
              <span style="color: #666;margin-left: 12px; font-weight: 500;">${data.value}%</span>
            </div>
          </div>`
      },
    },
    legend: {
      right: '5%',
      selectedMode: false,
    },
    grid: {
      left: '4%',
      right: '1%',
      bottom: '10px',
      top: '10px',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      inverse: true,
      minInterval: 1,
      // max: 100,
    },
    yAxis: {
      type: 'category',
      position: 'right',
      data: caseLevelData?.map(item => getCaseLevelMap()[item.level]),
      axisTick: {
        // 不要刻度线
        show: false,
      },
      axisLabel: {
        show: true,
        formatter: function (val) {
          if (val.length > 4) {
            val = `${val.substring(0, 4)}...`
          }
          return val
        },
        // fontSize: 10,
      },
      inverse: true,
    },
    series: {
      type: 'bar',
      barWidth: '21px',
      data:
        caseLevelData.length > 0
          ? caseLevelData.map(item => ({
              name: getCaseLevelMap()[item.level],
              value: item.failRate / 100,
              failCount: item.failCount,
            }))
          : [],
      label: {
        show: false,
        position: 'left',
      },
      itemStyle: {
        color: '#F44F4F',
      },
    },
  })

  const caseLevelSuccessOption = () => ({
    tooltip: {
      trigger: 'item',
      confine: true,
      axisPointer: {
        type: 'shadow',
      },
      formatter: function (params: any) {
        console.log(params, 'params')
        const data = params.data
        return `<div>
            <div style="margin-bottom: 4px">${data.name}</div>
            <div style="margin-bottom: 4px; color: #666;">
              <span>{t('拦截成功数')}: </span>
              <span style="font-weight: 500;">${data.interceptCount}</span>
            </div>
            ${
              deviceCount > 0
                ? `
            <div style="margin-bottom: 4px; color: #666;">
              <span>{t('检测成功数')}: </span>
              <span style="font-weight: 500;">${data.detectCount}</span>
            </div>
            <div>
              <span style="display: inline-block; margin-right: 8px; width: 6px; height: 6px; background-color: #88C56B; border-radius: 50%;"></span>
              <span style="color: #666;">{t('检测成功率')}</span>
              <span style="color: #666;margin-left: 12px; font-weight: 500;">${data.detectRate}%</span>
            </div>
            `
                : ''
            }
            <div>
              <span style="display: inline-block; margin-right: 8px; width: 6px; height: 6px; background-color: #73A0FA; border-radius: 50%;"></span>
              <span style="color: #666;">{t('拦截成功率')}</span>
              <span style="color: #666;margin-left: 12px; font-weight: 500;">${
                data.interceptRate
              }%</span>
            </div>
          </div>`
      },
    },
    legend: {
      right: '5%',
      selectedMode: false,
    },
    grid: {
      left: '4%',
      right: '4%',
      bottom: '10px',
      top: '10px',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      minInterval: 1,
      // max: 100,
    },
    yAxis: {
      type: 'category',
      data: caseLevelData?.map(item => getCaseLevelMap()[item.level]),
      axisTick: {
        // 不要刻度线
        show: false,
      },
      axisLabel: {
        show: false,
        formatter: function (val) {
          if (val.length > 4) {
            val = `${val.substring(0, 4)}...`
          }
          return val
        },
      },
      inverse: true,
    },
    series: [
      {
        type: 'bar',
        stack: 'total',
        barWidth: '21px',
        data:
          caseLevelData.length > 0
            ? caseLevelData.map(item => ({
                name: getCaseLevelMap()[item.level],
                value: item.detectRate / 100,
                detectRate: item.detectRate / 100,
                detectCount: item.detectCount,
                interceptRate: item.interceptRate / 100,
                interceptCount: item.interceptCount,
              }))
            : [],
        label: {
          show: false,
        },
        itemStyle: {
          color: '#88C56B',
        },
      },
      {
        type: 'bar',
        stack: 'total',
        barWidth: '21px',
        data:
          caseLevelData.length > 0
            ? caseLevelData.map(item => ({
                name: getCaseLevelMap()[item.level],
                value: item.interceptRate / 100,
                detectRate: item.detectRate / 100,
                detectCount: item.detectCount,
                interceptRate: item.interceptRate / 100,
                interceptCount: item.interceptCount,
              }))
            : [],
        label: {
          show: false,
        },
        itemStyle: {
          color: '#73A0FA',
        },
      },
    ],
  })

  const riskLevelFailOption = () => ({
    tooltip: {
      trigger: 'item',
      confine: true,
      axisPointer: {
        type: 'shadow',
      },
      formatter: function (params: any) {
        console.log(params, 'params')
        const data = params.data
        return `<div>
            <div style="margin-bottom: 4px">${data.name}</div>
            <div style="margin-bottom: 4px; color: #666;">
              <span>{t('防护失效数')}: </span>
              <span style="font-weight: 500;">${data.failCount}</span>
            </div>
            <div>
              <span style="display: inline-block; margin-right: 8px; width: 6px; height: 6px; background-color: ${params.color}; border-radius: 50%;"></span>
              <span style="color: #666;">{t('防护失效率')}</span>
              <span style="color: #666;margin-left: 12px; font-weight: 500;">${data.value}%</span>
            </div>
          </div>`
      },
    },
    legend: {
      right: '5%',
      selectedMode: false,
    },
    grid: {
      left: '4%',
      right: '1%',
      bottom: '10px',
      top: '10px',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      inverse: true,
      minInterval: 1,
      // max: 100,
    },
    yAxis: {
      type: 'category',
      position: 'right',
      data: riskLevelData?.map(item => getRiskLevelMap()[item.level]),
      axisTick: {
        // 不要刻度线
        show: false,
      },
      axisLabel: {
        show: true,
        formatter: function (val) {
          if (val.length > 4) {
            val = `${val.substring(0, 4)}...`
          }
          return val
        },
        // fontSize: 10,
      },
      inverse: true,
    },
    series: {
      type: 'bar',
      barWidth: '21px',
      data:
        riskLevelData.length > 0
          ? riskLevelData.map(item => ({
              name: getRiskLevelMap()[item.level],
              value: item.failRate / 100,
              failCount: item.failCount,
            }))
          : [],
      label: {
        show: false,
        position: 'left',
      },
      itemStyle: {
        color: '#F44F4F',
      },
    },
  })

  const riskLevelSuccessOption = () => ({
    tooltip: {
      trigger: 'item',
      confine: true,
      axisPointer: {
        type: 'shadow',
      },
      formatter: function (params: any) {
        console.log(params, 'params')
        const data = params.data
        return `<div>
            <div style="margin-bottom: 4px">${data.name}</div>
            <div style="margin-bottom: 4px; color: #666;">
              <span>{t('拦截成功数')}: </span>
              <span style="font-weight: 500;">${data.interceptCount}</span>
            </div>
            ${
              deviceCount > 0
                ? `
            <div style="margin-bottom: 4px; color: #666;">
              <span>{t('检测成功数')}: </span>
              <span style="font-weight: 500;">${data.detectCount}</span>
            </div>
            <div>
              <span style="display: inline-block; margin-right: 8px; width: 6px; height: 6px; background-color: #88C56B; border-radius: 50%;"></span>
              <span style="color: #666;">{t('检测成功率')}</span>
              <span style="color: #666;margin-left: 12px; font-weight: 500;">${data.detectRate}%</span>
            </div>
            `
                : ''
            }
            <div>
              <span style="display: inline-block; margin-right: 8px; width: 6px; height: 6px; background-color: #73A0FA; border-radius: 50%;"></span>
              <span style="color: #666;">{t('拦截成功率')}</span>
              <span style="color: #666;margin-left: 12px; font-weight: 500;">${
                data.interceptRate
              }%</span>
            </div>
          </div>`
      },
    },
    legend: {
      right: '5%',
      selectedMode: false,
    },
    grid: {
      left: '4%',
      right: '4%',
      bottom: '10px',
      top: '10px',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      minInterval: 1,
      // max: 100,
    },
    yAxis: {
      type: 'category',
      axisTick: {
        // 不要刻度线
        show: false,
      },
      axisLabel: {
        show: false,
        formatter: function (val) {
          if (val.length > 4) {
            val = `${val.substring(0, 4)}...`
          }
          return val
        },
      },
      inverse: true,
    },
    series: [
      {
        type: 'bar',
        stack: 'total',
        barWidth: '21px',
        data:
          riskLevelData.length > 0
            ? riskLevelData.map(item => ({
                name: getRiskLevelMap()[item.level],
                value: item.detectRate / 100,
                interceptRate: item.interceptRate / 100,
                detectRate: item.detectRate / 100,
                detectCount: item.detectCount,
                interceptCount: item.interceptCount,
              }))
            : [],
        label: {
          show: false,
        },
        itemStyle: {
          color: '#88C56B',
        },
      },
      {
        type: 'bar',
        stack: 'total',
        barWidth: '21px',
        data:
          riskLevelData.length > 0
            ? riskLevelData.map(item => ({
                name: getRiskLevelMap()[item.level],
                value: item.interceptRate / 100,
                interceptRate: item.interceptRate / 100,
                detectRate: item.detectRate / 100,
                detectCount: item.detectCount,
                interceptCount: item.interceptCount,
              }))
            : [],
        label: {
          show: false,
        },
        itemStyle: {
          color: '#73A0FA',
        },
      },
    ],
  })

  const templateProtectionFailOption = () => ({
    tooltip: {
      trigger: 'item',
      confine: true,
      axisPointer: {
        type: 'shadow',
      },
      formatter: function (params: any) {
        console.log(params, 'params')
        const data = params.data
        return `<div>
            <div style="margin-bottom: 4px">${data.name}</div>
            <div style="margin-bottom: 4px; color: #666;">
              <span>{t('防护失效数')}: </span>
              <span style="font-weight: 500;">${data.failCount}</span>
            </div>
            <div>
              <span style="display: inline-block; margin-right: 8px; width: 6px; height: 6px; background-color: ${params.color}; border-radius: 50%;"></span>
              <span style="color: #666;">{t('防护失效率')}</span>
              <span style="color: #666;margin-left: 12px; font-weight: 500;">${data.failRate}%</span>
            </div>
          </div>`
      },
    },
    legend: {
      right: '5%',
      selectedMode: false,
    },
    grid: {
      left: '4%',
      right: '1%',
      bottom: '10px',
      top: '10px',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      inverse: true,
      minInterval: 1,
      // max: 100,
    },
    yAxis: {
      type: 'category',
      position: 'right',
      data: templateProtectionData?.map(item => item.name),
      axisTick: {
        // 不要刻度线
        show: false,
      },
      axisLabel: {
        show: true,
        formatter: function (val) {
          if (val.length > 4) {
            val = `${val.substring(0, 4)}...`
          }
          return val
        },
        // fontSize: 10,
      },
      inverse: true,
    },
    series: {
      type: 'bar',
      barWidth: '21px',
      data:
        templateProtectionData.length > 0
          ? templateProtectionData.map(item => ({
              name: item.name,
              value: item.failRate / 100,
              failCount: item.failCount,
              failRate: item.failRate / 100,
            }))
          : [],
      label: {
        show: false,
        position: 'left',
      },
      itemStyle: {
        color: '#F44F4F',
      },
    },
  })
  const templateProtectionSuccessOption = () => ({
    tooltip: {
      trigger: 'item',
      confine: true,
      axisPointer: {
        type: 'shadow',
      },
      formatter: function (params: any) {
        console.log(params, 'params')
        const data = params.data
        return `<div>
            <div style="margin-bottom: 4px">${data.name}</div>
            <div style="margin-bottom: 4px; color: #666;">
              <span>{t('拦截成功数')}: </span>
              <span style="font-weight: 500;">${data.interceptCount}</span>
            </div>
            ${
              deviceCount > 0
                ? `
            <div style="margin-bottom: 4px; color: #666;">
              <span>{t('检测成功数')}: </span>
              <span style="font-weight: 500;">${data.detectCount}</span>
            </div>
            <div>
              <span style="display: inline-block; margin-right: 8px; width: 6px; height: 6px; background-color: #88C56B; border-radius: 50%;"></span>
              <span style="color: #666;">{t('检测成功率')}</span>
              <span style="color: #666;margin-left: 12px; font-weight: 500;">${data.detectRate}%</span>
            </div>
            `
                : ''
            }
            <div>
              <span style="display: inline-block; margin-right: 8px; width: 6px; height: 6px; background-color: #73A0FA; border-radius: 50%;"></span>
              <span style="color: #666;">{t('拦截成功率')}</span>
              <span style="color: #666;margin-left: 12px; font-weight: 500;">${
                data.interceptRate
              }%</span>
            </div>
          </div>`
      },
    },
    legend: {
      right: '5%',
      selectedMode: false,
    },
    grid: {
      left: '4%',
      right: '4%',
      bottom: '10px',
      top: '10px',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      minInterval: 1,
      // max: 100,
    },
    yAxis: {
      type: 'category',
      data: templateProtectionData?.map(item => item.name),
      axisTick: {
        // 不要刻度线
        show: false,
      },
      axisLabel: {
        show: false,
        formatter: function (val) {
          if (val.length > 4) {
            val = `${val.substring(0, 4)}...`
          }
          return val
        },
      },
      inverse: true,
    },
    series: [
      {
        type: 'bar',
        stack: 'total',
        barWidth: '21px',
        data:
          templateProtectionData.length > 0
            ? templateProtectionData.map(item => ({
                name: item.name,
                value: item.detectRate / 100,
                detectCount: item.detectCount,
                detectRate: item.detectRate / 100,
                interceptCount: item.interceptCount,
                interceptRate: item.interceptRate / 100,
              }))
            : [],
        label: {
          show: false,
        },
        itemStyle: {
          color: '#88C56B',
        },
      },
      {
        type: 'bar',
        stack: 'total',
        barWidth: '21px',
        data:
          templateProtectionData.length > 0
            ? templateProtectionData.map(item => ({
                name: item.name,
                value: item.interceptRate / 100,
                detectCount: item.detectCount,
                detectRate: item.detectRate / 100,
                interceptCount: item.interceptCount,
                interceptRate: item.interceptRate / 100,
              }))
            : [],
        label: {
          show: false,
        },
        itemStyle: {
          color: '#73A0FA',
        },
      },
    ],
  })

  const deviceProtectionRankOption = () => ({
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      backgroundColor: '#fff',
      // borderColor: '#42ACC2',
      borderWidth: 1,
      padding: [8, 12],
      textStyle: {
        color: '#333',
      },
      formatter: function (params: any) {
        const data = params[0].data
        return `<div>
            <div style="margin-bottom: 4px">${data.name}</div>
            <div style="margin-bottom: 4px; color: #666;">
              <span>IP: </span>
              <span style="font-weight: 500;">${data.ip || '-'}</span>
            </div>
            <div>
              <span style="display: inline-block; margin-right: 8px; width: 6px; height: 6px; background-color: ${
                params[0].color
              }; border-radius: 50%;"></span>
              <span style="color: #666;">拦截率</span>
              <span style="color: #666;margin-left: 12px; font-weight: 500;">${
                params[0]?.value
              }%</span>
            </div>
            <div>
              <span style="display: inline-block; margin-right: 8px; width: 6px; height: 6px; background-color: ${
                params[1]?.color
              }; border-radius: 50%;"></span>
              <span style="color: #666;">检测率</span>
              <span style="color: #666;margin-left: 12px; font-weight: 500;">${
                params[1]?.value
              }%</span>
            </div>
          </div>`
      },
    },
    grid: {
      left: '0%',
      right: '4%',
      bottom: '30px',
      top: '10px',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      max: 100,
      splitLine: {
        lineStyle: {
          type: 'dashed',
        },
      },
    },
    yAxis: {
      type: 'category',
      data: deviceProtectionData.map(item => item.deviceName),
      inverse: true,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        width: 200,
        overflow: 'truncate',
        formatter: function (value: string) {
          return `{device|${value}}`
        },
        rich: {
          device: {
            padding: [0, 0, 0, 0],
            cursor: 'pointer',
          },
        },
      },
      triggerEvent: true,
      axisPointer: {
        type: 'none',
      },
    },
    series: [
      {
        name: t('拦截率'),
        type: 'bar',
        stack: 'total',
        data:
          deviceProtectionData.length > 0
            ? deviceProtectionData.map(item => ({
                value: item.interceptionRate / 100,
                name: item.deviceName,
                ip: item.deviceIp,
              }))
            : [],
        barWidth: '21px',
        itemStyle: {
          color: '#EE6666',
        },
      },
      {
        name: t('检测率'),
        type: 'bar',
        stack: 'total',
        data:
          deviceProtectionData.length > 0
            ? deviceProtectionData.map(item => ({
                value: item.detectionRate / 100,
                name: item.deviceName,
                ip: item.deviceIp,
              }))
            : [],
        barWidth: '21px',
        itemStyle: {
          color: '#88C56B',
        },
      },
    ],
  })

  const chunk = <T,>(arr: T[], size: number): T[][] => {
    const res: T[][] = []
    for (let i = 0; i < arr.length; i += size) {
      res.push(arr.slice(i, i + size))
    }
    return res
  }

  const attackSlides = useMemo(
    () => chunk(attackProtectionRanking, chunkSize),
    [attackProtectionRanking],
  )

  const attachmentSlides = useMemo(
    () => chunk(attachmentTypeRanking, chunkSize),
    [attachmentTypeRanking],
  )

  return (
    <ChartContainer>
      <div className="top">
        <div className="left">
          <div className="task-score-wrapper">
            <div className="task-score">
              <ReactECharts
                style={{ height: '180px' }}
                option={scoreOption({
                  value: Number(score) / 100 || 0,
                  title:
                    (Number(score) / 100 <= 100 && Number(score) / 100 >= 80 && t('优秀')) ||
                    (Number(score) / 100 < 80 && Number(score) / 100 >= 60 && t('良好')) ||
                    (Number(score) / 100 < 60 && Number(score) / 100 >= 40 && t('一般')) ||
                    (Number(score) / 100 < 40 && Number(score) / 100 >= 20 && t('较弱')) ||
                    t('弱'),
                })}
              />
            </div>
            <div className="tag">{t('任务评分')}</div>
          </div>
          <div className="fail-rate">
            <ReactECharts style={{ height: '230px' }} option={failRateOption()} />
          </div>
        </div>
        <div className="right">
          <div
            className="protected-rate"
            style={{ marginRight: attachmentSlides.length > 0 ? '10px' : '0' }}
          >
            <span className="title">{t('攻击防护排名')}</span>
            <span className="sub-title">{t('各攻击类型拦截率排名')}</span>
            {attackSlides.length > 0 ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '20px' }}>
                  <Swiper
                    style={{ width: '100%' }}
                    spaceBetween={20}
                    loop={attackSlides.length > 1}
                    direction="vertical"
                    autoplay={{
                      delay: 3000,
                    }}
                  >
                    {attackSlides.map((group, idx) => (
                      <SwiperSlide key={idx}>
                        <div className="encode-container">
                          {group.map((item, i) => (
                            <RankItem key={i} index={idx * chunkSize + i + 1}>
                              <div className="left">
                                <div className="num">{idx * chunkSize + i + 1}</div>
                                <Typography.Text title={item.attackName}>
                                  <span
                                    className="type"
                                    style={{
                                      display: 'inline-block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {item.attackName}
                                  </span>
                                </Typography.Text>
                              </div>
                              <span className="rate">
                                {item?.rate?.toString()?.length && `${Number(item.rate) / 100}%`}
                              </span>
                            </RankItem>
                          ))}
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
                <div className="less-pag" style={{ textAlign: 'center', color: '#86909C' }} />
              </div>
            ) : (
              <Empty image={EmptyBarGraph} imageStyle={{ marginTop: '65px' }} description={false} />
            )}
          </div>
          {attachmentSlides.length > 0 ? (
            <div className="encode-rate">
              <span className="title">{t('附件类型排名')}</span>
              <span className="sub-title">{t('各附件类型拦截成功率排名')}</span>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '20px' }}>
                  <Swiper
                    style={{ width: '100%' }}
                    spaceBetween={20}
                    loop={attachmentSlides.length > 1}
                    direction="vertical"
                    autoplay={{
                      delay: 3000,
                    }}
                  >
                    {attachmentSlides.map((group, idx) => (
                      <SwiperSlide key={idx}>
                        <div className="encode-container">
                          {group.map((item, i) => (
                            <RankItem key={i} index={idx * chunkSize + i + 1}>
                              <div className="left">
                                <div className="num">{idx * chunkSize + i + 1}</div>
                                <span className="type">{item.attachmentType}</span>
                              </div>
                              <span className="rate">
                                {item?.rate?.toString()?.length && `${item.rate / 100}%`}
                              </span>
                            </RankItem>
                          ))}
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
                <div className="encode-pag" style={{ textAlign: 'center', color: '#86909C' }} />
              </div>
            </div>
          ) : (
            <Empty image={EmptyBarGraph} imageStyle={{ marginTop: '65px' }} description={false} />
          )}
        </div>
      </div>
      <div className="center">
        <div className="left">
          <div>
            <span
              style={{
                fontSize: '14px',
                marginLeft: '3px',
              }}
            >
              {t('用例等级分布')}
            </span>
          </div>
          <div>
            {caseLevelData?.length > 0 ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    height: 200,
                    overflowY: 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <div style={{ width: '53%' }}>
                    <ReactECharts
                      style={{
                        height: 198,
                      }}
                      option={caseLevelFailOption()}
                    />
                  </div>
                  <div style={{ width: '47%' }}>
                    <ReactECharts
                      style={{
                        height: 198,
                      }}
                      option={caseLevelSuccessOption()}
                    />
                  </div>
                </div>
                <div className="legends">
                  <div className="legend">
                    <div className="tag red" />
                    <div>{t('防护失效')}</div>
                  </div>
                  {deviceCount > 0 && (
                    <div className="legend">
                      <div className="tag green" />
                      <div>{t('检测成功')}</div>
                    </div>
                  )}
                  <div className="legend">
                    <div className="tag blue" />
                    <div>{t('拦截成功')}</div>
                  </div>
                </div>
              </>
            ) : (
              <Empty image={EmptyBarGraph} imageStyle={{ marginTop: '65px' }} description={false} />
            )}
          </div>
        </div>
        <div className="right">
          <div>
            <span
              style={{
                fontSize: '14px',
                marginLeft: '3px',
              }}
            >
              {t('危险等级分布')}
            </span>
          </div>
          <div>
            {riskLevelData?.length > 0 ? (
              <>
                <div style={{ display: 'flex' }}>
                  <div style={{ width: '45%' }}>
                    <ReactECharts style={{ height: 198 }} option={riskLevelFailOption()} />
                  </div>
                  <div style={{ width: '45%' }}>
                    <ReactECharts style={{ height: 198 }} option={riskLevelSuccessOption()} />
                  </div>
                </div>
                <div className="legends">
                  <div className="legend">
                    <div className="tag red" />
                    <div>{t('防护失效')}</div>
                  </div>
                  {deviceCount > 0 && (
                    <div className="legend">
                      <div className="tag green" />
                      <div>{t('检测成功')}</div>
                    </div>
                  )}
                  <div className="legend">
                    <div className="tag blue" />
                    <div>{t('拦截成功')}</div>
                  </div>
                </div>
              </>
            ) : (
              <Empty image={EmptyBarGraph} imageStyle={{ marginTop: '65px' }} description={false} />
            )}
          </div>
        </div>
      </div>
      <div className="bottom">
        <div className="left">
          <div>
            <span
              style={{
                fontSize: '14px',
                marginLeft: '3px',
              }}
            >
              {t('模板防护失效率排名')}
            </span>
          </div>
          {templateProtectionData?.length > 0 ? (
            <>
              <div
                style={{
                  display: 'flex',
                  height: 200,
                  overflowY: 'auto',
                  justifyContent: 'center',
                }}
              >
                <div style={{ width: '53%' }}>
                  <ReactECharts
                    style={{
                      height:
                        templateProtectionData?.length > 5
                          ? templateProtectionData?.length * 30
                          : 210,
                    }}
                    option={templateProtectionFailOption()}
                  />
                </div>
                <div style={{ width: '47%' }}>
                  <ReactECharts
                    style={{
                      height:
                        templateProtectionData?.length > 5
                          ? templateProtectionData?.length * 30
                          : 210,
                    }}
                    option={templateProtectionSuccessOption()}
                  />
                </div>
              </div>
              <div className="legends">
                <div className="legend">
                  <div className="tag red" />
                  <div>防护失效</div>
                </div>
                {deviceCount > 0 && (
                  <div className="legend">
                    <div className="tag green" />
                    <div>检测成功</div>
                  </div>
                )}
                <div className="legend">
                  <div className="tag blue" />
                  <div>拦截成功</div>
                </div>
              </div>
            </>
          ) : (
            <Empty image={EmptyBarGraph} imageStyle={{ marginTop: '65px' }} description={false} />
          )}
        </div>
        {deviceProtectionData?.length > 0 && (
          <div className="right">
            <div>
              <span
                style={{
                  fontSize: '14px',
                  marginLeft: '3px',
                }}
              >
                {t('设备防护能力排名')}
              </span>
            </div>

            <>
              <div>
                <div>
                  <ReactECharts
                    style={{
                      height:
                        deviceProtectionData?.length > 5 ? deviceProtectionData?.length * 30 : 210,
                    }}
                    option={deviceProtectionRankOption()}
                  />
                </div>
              </div>
              <div className="legends">
                <div className="legend">
                  <div className="tag red" />
                  <div>{t('拦截率')}</div>
                </div>
                <div className="legend">
                  <div className="tag green" />
                  <div>{t('检测率')}</div>
                </div>
              </div>
            </>
          </div>
        )}
      </div>
    </ChartContainer>
  )
}

export default TaskCharts

const ChartContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  .carousel-item {
    padding: 0 2px;
  }

  .top {
    width: 100%;
    min-height: 245px;
    margin-bottom: 30px;
    display: flex;

    .left {
      width: 50%;
      display: flex;
      // min-width: 676px;

      .task-score-wrapper {
        width: 45%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;

        .task-score {
          width: 100%;
          height: 100%;
        }

        .tag {
          width: 92px;
          height: 24px;
          background: rgba(115, 160, 250, 0.1);
          border-radius: 4px;
          border: 1px solid #73a0fa;
          color: #73a0fa;
          font-size: 14px;
          line-height: 22px;
          text-align: center;
        }
      }

      .fail-rate {
        width: 40%;
        height: 100%;
        margin-left: 20px;
      }

      .decode-rate {
        width: 40%;
        height: 100%;
      }
    }

    .right {
      width: 49.6%;
      display: flex;
      justify-content: space-between;

      .protected-rate,
      .encode-rate {
        width: 100%;
        height: 246px;
        background: #f8f9fb;
        border-radius: 8px;
        padding: 12px 10px 12px 16px;
        overflow: hidden;
        margin-right: 10px;

        .title {
          font-size: 14px;
          font-weight: 500;
          margin-right: 8px;
        }

        .sub-title {
          font-size: 12px;
          color: #666;
        }

        .carousel {
          position: relative;
          padding: 0 10px;

          .prev-btn-wrapper {
            position: absolute;
            top: 50%;
            left: -10px;
            transform: translateY(-50%);
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #d9d9d9;
            display: flex;
            justify-content: center;
            align-items: center;
          }

          .prev-btn {
            font-size: 12px;
            color: #fff;

            &:hover {
              cursor: pointer;
            }
          }

          .next-btn-wrapper {
            position: absolute;
            top: 50%;
            right: -10px;
            transform: translateY(-50%);
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #d9d9d9;
            display: flex;
            justify-content: center;
            align-items: center;
          }

          .next-btn {
            font-size: 12px;
            color: #fff;

            &:hover {
              cursor: pointer;
            }
          }
        }
      }
    }
  }

  .center {
    width: 100%;
    height: 260px;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .left {
      width: 50%;
      // min-width: 676px;
      height: 100%;
      margin-right: 16px;
      background: #f8f9fb;
      border-radius: 8px;
      padding: 10px 16px 0 16px;
      overflow-y: scroll;
    }

    .right {
      width: 50%;
      height: 100%;
      background: #f8f9fb;
      border-radius: 8px;
      padding: 10px 16px 0 16px;
    }
    .legends {
      display: flex;
      justify-content: center;

      .legend {
        font-size: 12px;
        display: flex;
        margin: 10px 12px;
        align-items: center;
        color: #333333;

        .tag {
          width: 18px;
          height: 12px;
          border-radius: 2px;
          margin-right: 5px;
        }

        div {
          line-height: 12px;
        }

        .red {
          background: #f44f4f;
        }

        .blue {
          background: #73a0fa;
        }
        .green {
          background: #88c56b;
        }
        .pink {
          background: #f6eef2;
        }
      }
    }

    .labels {
      div {
        color: #989ba3;
        font-size: 12px;
        margin-top: 24px;
      }
    }
  }

  .bottom {
    width: 100%;
    min-height: 260px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .left {
      width: 50%;
      // min-width: 676px;
      margin-right: 16px;
      background: #f8f9fb;
      border-radius: 8px;
      padding: 10px 16px 0 16px;
      overflow-y: scroll;
    }

    .legends {
      display: flex;
      justify-content: center;

      .legend {
        font-size: 12px;
        display: flex;
        margin: 10px 12px;
        align-items: center;
        color: #333333;

        .tag {
          width: 18px;
          height: 12px;
          border-radius: 2px;
          margin-right: 5px;
        }

        div {
          line-height: 12px;
        }

        .red {
          background: #f44f4f;
        }

        .blue {
          background: #73a0fa;
        }

        .green {
          background: #88c56b;
        }

        .pink {
          background: #fcdcdc;
        }
      }
    }

    .right {
      width: 50%;
      // min-width: 676px;
      background: #f8f9fb;
      border-radius: 8px;
      padding: 10px 16px 0 16px;
    }
  }
`

const ItemChild = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;

  .tag {
    display: inline-block;
    width: 14px;
    height: 14px;
    background: ${({ index }) =>
      index === 0
        ? 'linear-gradient(180deg, #fe6e2f 0%, #fe3101 100%)'
        : index === 1
        ? 'linear-gradient(180deg, #f34f50 0%, #f31700 100%)'
        : index === 2
        ? 'linear-gradient( 180deg, #FED837 0%, #FEB107 100%)'
        : '#8D94A8'};
    border-radius: 4px;
    font-size: 12px;
    color: #fff;
    line-height: 14px;
    text-align: center;
    margin-right: 6px;
  }

  .rate {
    color: #f5222d;
  }
`

interface RankItemProps {
  index: boolean
}

const RankItem = styled.div<RankItemProps>`
  margin-bottom: 25px;
  display: flex;
  justify-content: space-between;
  .left {
    display: flex;
    line-height: 14px;
    width: 85% !important;
  }
  .num {
    font-size: 12px;
    border-radius: 4px;
    height: 14px;
    width: 20px;
    text-align: center;
    color: #fff;
    margin-right: 4px;
    background: ${props =>
      props.index === 1 && 'linear-gradient(180deg, #FE6E2F 0%, #FE3101 100%)'};
    background: ${props =>
      props.index === 2 && 'linear-gradient(180deg, #FEAE1E 0%, #FE7900 100%)'};
    background: ${props =>
      props.index === 3 && 'linear-gradient(180deg, #FED837 0%, #FEB107 100%)'};
    background: ${props => props.index > 3 && '#8D94A8'};
  }

  .type {
    height: 14px;
    font-size: 14px;
    font-weight: 400;
    color: #333333;
    line-height: 14px;
  }

  .rate {
    height: 14px;
    font-size: 14px;
    font-weight: 400;
    color: #f5222d;
  }
`
